import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { PlannableUser, StammkundeMember, StammkundeRole } from '../../../Models/stammkunde.model';

export interface TeamFormData {
  customerId: number;
  members: StammkundeMember[];
}

interface MemberRow {
  userId: string;
  role: StammkundeRole;
}

/** Popup zur Team-Pflege eines Stammkunden (nur SuperAdmin): wer ist Bauleiter/Vorarbeiter. */
@Component({
  selector: 'app-team-form',
  templateUrl: './team-form.component.html',
  styleUrls: ['./team-form.component.css', '../stammkunden-theme.css']
})
export class TeamFormComponent implements OnInit {
  rows: MemberRow[] = [];
  users: PlannableUser[] = [];
  loadingUsers = true;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<TeamFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TeamFormData,
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar
  ) {
    this.rows = (data.members ?? []).map(m => ({ userId: m.userId, role: m.role }));
  }

  ngOnInit(): void {
    this.stammkundeService.getPlannableUsers().subscribe({
      next: users => { this.users = users ?? []; this.loadingUsers = false; },
      error: () => {
        this.users = [];
        this.loadingUsers = false;
        // Ohne Hinweis sähen die bestehenden Zuordnungen wie gelöscht aus.
        this.snackBar.open('Fehler beim Laden der Benutzerliste.', 'OK', { duration: 5000 });
      }
    });
  }

  addRow(role: StammkundeRole = 'Vorarbeiter'): void {
    this.rows.push({ userId: '', role });
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
  }

  userLabel(user: PlannableUser): string {
    return user.userName || user.email || user.id;
  }

  get valid(): boolean {
    // Jede Zeile braucht einen Benutzer; kein Benutzer doppelt.
    if (this.rows.some(r => !r.userId)) return false;
    return new Set(this.rows.map(r => r.userId)).size === this.rows.length;
  }

  save(): void {
    if (this.rows.some(r => !r.userId)) {
      this.snackBar.open('Bitte für jede Zeile einen Benutzer wählen (oder die Zeile entfernen).', 'OK', { duration: 4000 });
      return;
    }
    if (!this.valid) {
      this.snackBar.open('Ein Benutzer kann nur eine Rolle je Stammkunde haben.', 'OK', { duration: 4000 });
      return;
    }
    this.saving = true;
    const payload: StammkundeMember[] = this.rows
      .map(r => ({ userId: r.userId, role: r.role }));

    this.stammkundeService.setMembers(this.data.customerId, payload).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: err => {
        this.saving = false;
        const message = err?.error?.message || 'Fehler beim Speichern des Teams.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
